// What an Edit-routine Save actually writes, and what an accepted edit does to the cached routine.
//
// Both rules are pure and live here — with NO runtime imports — so they are unit-testable (see the
// "Tests (jest)" rule in CLAUDE.md) and so the three surfaces can be held to the same table. Mirrored
// 1:1 by iOS `RoutineDetailScreen.routineUpdate` + `SymphonyStore.applyRoutineChange` and Android
// `routineEdit` + `SymphonyStore.applyRoutineChange`; all three unit-test the same cases.
//
// The two distinctions that make this a module rather than an object literal at the call site:
//
//   1. **Only what CHANGED travels.** The server answers `400 no_updates` to an empty write, and a write
//      that restates unchanged values clobbers a concurrent edit the agent's own tools may have made.
//   2. **`cron: ''` is a real edit, not "unchanged".** Clearing the schedule is the supported way to make
//      a routine trigger-only, so blank cannot stand in for untouched — while a routine that never had a
//      cron must not post `cron: ''` on every Save. `undefined` is the only "don't touch it".
//
// A blank NAME is the one field that is never an edit: a routine with no name is not a routine, so Save
// is blocked (`canSaveRoutineEdit`) rather than writing an empty title.

/** The fields to send. An absent key means "don't touch it". */
export interface RoutineEdit {
  name?: string;
  description?: string;
  cron?: string;
}

/** What the user has typed, before anything is written. */
export interface RoutineEditDraft {
  name: string;
  description: string;
  cron: string;
}

/** The stored routine, as far as diffing is concerned. */
export interface EditableRoutine {
  name?: string;
  description?: string;
  cron?: string;
}

function trim(value?: string): string {
  return (value ?? '').trim();
}

/** The diff between the stored routine and the draft, or `null` when nothing changed. */
export function routineEdit(original: EditableRoutine, draft: RoutineEditDraft): RoutineEdit | null {
  const edit: RoutineEdit = {};
  const name = trim(draft.name);
  if (name && name !== trim(original.name)) edit.name = name;
  const description = trim(draft.description);
  if (description !== trim(original.description)) edit.description = description;
  const cron = trim(draft.cron);
  if (cron !== trim(original.cron)) edit.cron = cron;
  return Object.keys(edit).length > 0 ? edit : null;
}

/** Whether Save may be pressed at all. A routine with no name is not a routine. */
export function canSaveRoutineEdit(draft: { name: string }): boolean {
  return trim(draft.name).length > 0;
}

/** The cron-DERIVED fields, which the server computed and the client must not keep past a cron change. */
export interface CronDerivedRoutine {
  cron?: string;
  nextRun?: string | null;
  display?: Record<string, unknown>;
  /** Set by the BFF, one-way: the routine has been live at least once (see `routine-status`). */
  activated?: boolean;
}

/**
 * Apply an accepted edit to a cached routine.
 *
 * A cron change DROPS `nextRun` and the server-rendered `display` block, both of which were derived from
 * the cron that was just replaced. Keeping them leaves the card counting down to a run under a schedule
 * that no longer exists — the bug all three surfaces' edit tests were written to pin. The next routines
 * fetch brings back the real values.
 *
 * A RESUME adopts: the server reads every un-pause as adoption and never forgets it, and the cache
 * says so at once — otherwise a recommended routine switched on and off again before the next fetch
 * would jump back to the Recommended tab, which the server has already decided it has left for good.
 */
export function applyRoutineEdit<T extends CronDerivedRoutine>(
  routine: T,
  updates: RoutineEdit & { paused?: boolean },
): T {
  const next: T = { ...routine, ...updates };
  if (updates.cron !== undefined) {
    next.nextRun = null;
    delete next.display;
  }
  if (updates.paused === false) {
    next.activated = true;
  }
  return next;
}
